import { useCallback, useEffect, useRef } from "react";
import { ChargeLayer } from "@/pages/sy/models/ChargeLayer";
import { LayerAlignmentRow } from "../utils/layerAlignment";

const CONTEXT_ROWS_ABOVE = 3;
const SMOOTH_SCROLL_SUPPRESS_MS = 700;
const INSTANT_SCROLL_SUPPRESS_MS = 80;

type Side = "current" | "recipe";

interface LayerRef {
  layerNumber: number;
  recipeLayerId: number | null;
}

const otherSide = (side: Side): Side =>
  side === "current" ? "recipe" : "current";

const toLayerRef = (
  row: LayerAlignmentRow | undefined,
  side: Side,
): LayerRef | null => {
  const layer = side === "current" ? row?.chargeLayer : row?.recipeLayer;
  if (!layer) return null;
  return {
    layerNumber: layer.LayerNumber ?? 0,
    recipeLayerId: layer.RecipeLayerId ?? null,
  };
};

const findCounterpartIndex = (
  target: LayerRef,
  rows: LayerAlignmentRow[],
  side: Side,
): number => {
  const refs = rows.map((row) => toLayerRef(row, side));

  if (target.recipeLayerId != null) {
    const byRecipeLayer = refs.findIndex(
      (ref) => ref?.recipeLayerId === target.recipeLayerId,
    );
    if (byRecipeLayer >= 0) return byRecipeLayer;
  }

  const byNumber = refs.findIndex(
    (ref) => ref?.layerNumber === target.layerNumber,
  );
  if (byNumber >= 0) return byNumber;

  let closest = -1;
  let closestDistance = Number.POSITIVE_INFINITY;
  refs.forEach((ref, index) => {
    if (!ref) return;
    const distance = Math.abs(ref.layerNumber - target.layerNumber);
    if (distance < closestDistance) {
      closestDistance = distance;
      closest = index;
    }
  });
  return closest;
};

const getRows = (container: HTMLDivElement): HTMLTableRowElement[] =>
  Array.from(container.querySelector("tbody")?.rows ?? []);

const getHeaderHeight = (container: HTMLDivElement): number =>
  container.querySelector("thead")?.getBoundingClientRect().height ?? 0;

const getRowOffset = (
  container: HTMLDivElement,
  row: HTMLTableRowElement,
): number =>
  row.getBoundingClientRect().top - container.getBoundingClientRect().top;

export const useLayerScrollSync = ({
  chargeId,
  currentRows,
  recipeRows,
}: {
  chargeId?: string;
  currentRows: LayerAlignmentRow[];
  recipeRows: LayerAlignmentRow[];
}) => {
  const currentLayersRef = useRef<HTMLDivElement>(null);
  const recipeLayersRef = useRef<HTMLDivElement>(null);
  const rowsRef = useRef<Record<Side, LayerAlignmentRow[]>>({
    current: currentRows,
    recipe: recipeRows,
  });
  rowsRef.current = { current: currentRows, recipe: recipeRows };

  const suppressUntilRef = useRef<Record<Side, number>>({
    current: 0,
    recipe: 0,
  });
  const frameRef = useRef<number | null>(null);
  const bottomDoneRef = useRef<{
    chargeId?: string;
    current: boolean;
    recipe: boolean;
  }>({ current: false, recipe: false });

  const getContainer = useCallback(
    (side: Side) =>
      side === "current" ? currentLayersRef.current : recipeLayersRef.current,
    [],
  );

  const scrollContainerTo = useCallback(
    (side: Side, container: HTMLDivElement, top: number, smooth: boolean) => {
      if (Math.abs(top - container.scrollTop) < 1) return;
      suppressUntilRef.current[side] =
        performance.now() +
        (smooth ? SMOOTH_SCROLL_SUPPRESS_MS : INSTANT_SCROLL_SUPPRESS_MS);
      container.scrollTo({ top, behavior: smooth ? "smooth" : "auto" });
    },
    [],
  );

  const scrollRowIntoContext = useCallback(
    (side: Side, index: number) => {
      const container = getContainer(side);
      const row = container ? getRows(container)[index] : undefined;
      if (!container || !row) return;
      const headerHeight = getHeaderHeight(container);
      const rowHeight = row.getBoundingClientRect().height;
      const contextHeight = Math.max(
        0,
        Math.min(
          CONTEXT_ROWS_ABOVE * rowHeight,
          container.clientHeight - headerHeight - rowHeight,
        ),
      );
      scrollContainerTo(
        side,
        container,
        container.scrollTop +
          getRowOffset(container, row) -
          headerHeight -
          contextHeight,
        true,
      );
    },
    [getContainer, scrollContainerTo],
  );

  const scrollToChargeLayer = useCallback(
    (layer: ChargeLayer) => {
      const index = rowsRef.current.current.findIndex(
        (row) => row.chargeLayer?.ChargeLayerId === layer.ChargeLayerId,
      );
      const target = toLayerRef(rowsRef.current.current[index], "current");
      if (!target) return;
      scrollRowIntoContext("current", index);
      scrollRowIntoContext(
        "recipe",
        findCounterpartIndex(target, rowsRef.current.recipe, "recipe"),
      );
    },
    [scrollRowIntoContext],
  );

  const followScroll = useCallback(
    (lead: Side) => {
      const follower = otherSide(lead);
      const leadContainer = getContainer(lead);
      const followerContainer = getContainer(follower);
      if (!leadContainer || !followerContainer) return;

      const headerBottom =
        leadContainer.getBoundingClientRect().top +
        getHeaderHeight(leadContainer);
      const leadRows = getRows(leadContainer);
      const leadIndex = leadRows.findIndex(
        (row, index) =>
          row.getBoundingClientRect().bottom > headerBottom &&
          toLayerRef(rowsRef.current[lead][index], lead) !== null,
      );
      const target = toLayerRef(rowsRef.current[lead][leadIndex], lead);
      if (!target) return;

      const counterpartIndex = findCounterpartIndex(
        target,
        rowsRef.current[follower],
        follower,
      );
      const counterpart = getRows(followerContainer)[counterpartIndex];
      if (!counterpart) return;

      scrollContainerTo(
        follower,
        followerContainer,
        followerContainer.scrollTop +
          getRowOffset(followerContainer, counterpart) -
          getRowOffset(leadContainer, leadRows[leadIndex]),
        false,
      );
    },
    [getContainer, scrollContainerTo],
  );

  const handleScroll = useCallback(
    (side: Side) => {
      if (performance.now() < suppressUntilRef.current[side]) return;
      if (frameRef.current !== null) return;
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        followScroll(side);
      });
    },
    [followScroll],
  );

  const onCurrentLayersScroll = useCallback(
    () => handleScroll("current"),
    [handleScroll],
  );
  const onRecipeLayersScroll = useCallback(
    () => handleScroll("recipe"),
    [handleScroll],
  );

  useEffect(() => {
    if (!chargeId) return;
    if (bottomDoneRef.current.chargeId !== chargeId) {
      bottomDoneRef.current = { chargeId, current: false, recipe: false };
    }
    (["current", "recipe"] as Side[]).forEach((side) => {
      const container = getContainer(side);
      if (
        bottomDoneRef.current[side] ||
        !container ||
        rowsRef.current[side].length === 0
      ) {
        return;
      }
      scrollContainerTo(side, container, container.scrollHeight, false);
      bottomDoneRef.current[side] = true;
    });
  }, [
    chargeId,
    currentRows.length,
    recipeRows.length,
    getContainer,
    scrollContainerTo,
  ]);

  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  return {
    currentLayersRef,
    recipeLayersRef,
    onCurrentLayersScroll,
    onRecipeLayersScroll,
    scrollToChargeLayer,
  };
};
